import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  BarChart3, FileText, Image, MessageSquare, LogOut, Trash2, Pencil, Plus, X, Upload,
  Settings, Shield, Bell, Globe, Users, Star, ChevronUp, ChevronDown, Eye, EyeOff,
  Phone, Mail, MapPin, Clock, Hash, Link2, Facebook, Instagram, Linkedin, MessageCircle,
  Power, AlertTriangle, Save, Check
} from "lucide-react";
import axios from "axios";
import AdminChats from "@/pages/AdminChats";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const statusLabels = { nouveau: "Nouveau", en_cours: "En cours", traite: "Traite" };
const statusColors = { nouveau: "bg-blue-500", en_cours: "bg-yellow-500", traite: "bg-green-500" };

function Toggle({ checked, onChange, label, desc }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0' }}>
      <div>
        <p style={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>{label}</p>
        {desc && <p style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>{desc}</p>}
      </div>
      <button onClick={() => onChange(!checked)} data-testid={`toggle-${label?.toLowerCase().replace(/\s/g, '-')}`}
        style={{ width: '48px', height: '26px', borderRadius: '13px', backgroundColor: checked ? '#8B0000' : '#333', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.3s' }}>
        <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#fff', position: 'absolute', top: '3px', left: checked ? '25px' : '3px', transition: 'left 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.3)' }} />
      </button>
    </div>
  );
}

function SettingInput({ label, value, onChange, icon: Icon, placeholder, type = "text" }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#8B0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#0a0a0a', border: '1px solid #27272A', borderRadius: '6px', padding: '0 12px' }}>
        {Icon && <Icon size={16} color="#666" />}
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', padding: '10px 0', fontSize: '14px', outline: 'none' }} />
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, status }) {
  return (
    <div style={{ backgroundColor: '#141414', border: '1px solid #27272A', borderRadius: '10px', padding: '20px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {Icon && <Icon size={18} color="#8B0000" />}
          <h3 style={{ color: '#fff', fontSize: '16px', fontWeight: 800, fontFamily: "'Cabinet Grotesk', sans-serif", margin: 0 }}>{title}</h3>
        </div>
        {status !== undefined && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: 700, color: status ? '#4ade80' : '#f87171', textTransform: 'uppercase' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: status ? '#4ade80' : '#f87171' }} />
            {status ? 'Actif' : 'Inactif'}
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

function SaveBtn({ onClick, saving, saved }) {
  return (
    <button onClick={onClick} disabled={saving} data-testid="save-settings"
      style={{ backgroundColor: saved ? '#166534' : '#8B0000', color: '#fff', fontWeight: 700, fontSize: '13px', padding: '10px 24px', border: 'none', borderRadius: '6px', cursor: saving ? 'default' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: saving ? 0.6 : 1, transition: 'background 0.3s' }}>
      {saved ? <><Check size={14} /> Sauvegardé</> : saving ? "Sauvegarde..." : <><Save size={14} /> Sauvegarder</>}
    </button>
  );
}

export default function AdminDashboard() {
  const { user, token, loading: authLoading, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [settings, setSettings] = useState({});
  const [reviews, setReviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editReview, setEditReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ name: "", text: "", rating: 5, active: true });

  useEffect(() => { if (!authLoading && !user) navigate("/admin/login"); }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!token) return;
    const fetchAll = async () => {
      try {
        const [s, q, c, g, st, r] = await Promise.all([
          axios.get(`${API}/stats`, { headers: authHeaders }),
          axios.get(`${API}/quotes`, { headers: authHeaders }),
          axios.get(`${API}/contacts`, { headers: authHeaders }),
          axios.get(`${API}/gallery`, { headers: authHeaders }),
          axios.get(`${API}/settings`, { headers: authHeaders }),
          axios.get(`${API}/reviews`),
        ]);
        setStats(s.data); setQuotes(q.data); setContacts(c.data); setGallery(g.data); setSettings(st.data); setReviews(r.data);
      } catch (e) { console.error(e); }
    };
    fetchAll();
  }, [token, authHeaders]);

  const saveSettings = async () => {
    setSaving(true); setSaved(false);
    try {
      const res = await axios.put(`${API}/settings`, settings, { headers: authHeaders });
      setSettings(res.data); setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const updateSetting = (key, val) => setSettings(s => ({ ...s, [key]: val }));

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}/status`, { status }, { headers: authHeaders });
      setQuotes(qs => qs.map(q => q.id === id ? { ...q, status } : q));
    } catch (e) { console.error(e); }
  };
  const deleteQuote = async (id) => {
    try { await axios.delete(`${API}/quotes/${id}`, { headers: authHeaders }); setQuotes(qs => qs.filter(q => q.id !== id)); } catch (e) { console.error(e); }
  };

  // Reviews
  const saveReview = async () => {
    try {
      if (editReview) {
        const res = await axios.put(`${API}/reviews/${editReview.id}`, reviewForm, { headers: authHeaders });
        setReviews(r => r.map(rv => rv.id === editReview.id ? res.data : rv));
      } else {
        const res = await axios.post(`${API}/reviews`, reviewForm, { headers: authHeaders });
        setReviews(r => [...r, res.data]);
      }
      setEditReview(null); setReviewForm({ name: "", text: "", rating: 5, active: true });
    } catch (e) { console.error(e); }
  };
  const deleteReview = async (id) => {
    try { await axios.delete(`${API}/reviews/${id}`, { headers: authHeaders }); setReviews(r => r.filter(rv => rv.id !== id)); } catch (e) { console.error(e); }
  };
  const reorderReview = async (id, dir) => {
    try {
      await axios.patch(`${API}/reviews/${id}/reorder`, { direction: dir }, { headers: authHeaders });
      const res = await axios.get(`${API}/reviews`);
      setReviews(res.data);
    } catch (e) { console.error(e); }
  };
  const toggleReview = async (review) => {
    try {
      const res = await axios.put(`${API}/reviews/${review.id}`, { active: !review.active }, { headers: authHeaders });
      setReviews(r => r.map(rv => rv.id === review.id ? res.data : rv));
    } catch (e) { console.error(e); }
  };

  const filteredQuotes = filterStatus === "all" ? quotes : quotes.filter(q => q.status === filterStatus);

  if (authLoading) return <div style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p style={{ color: '#aaa' }}>Chargement...</p></div>;
  if (!user) return null;

  const tabs = [
    { id: "overview", label: "Vue d'ensemble", icon: BarChart3 },
    { id: "quotes", label: "Devis", icon: FileText },
    { id: "contacts", label: "Messages", icon: MessageSquare },
    { id: "chats", label: "Assistant IA", icon: MessageCircle },
    { id: "settings", label: "Paramètres", icon: Settings },
    { id: "reviews", label: "Avis", icon: Star },
    { id: "seo", label: "SEO", icon: Globe },
    { id: "photos", label: "Photos", icon: Upload },
    { id: "before-after", label: "Avant/Apres", icon: Image },
  ];

  return (
    <div data-testid="admin-dashboard" style={{ minHeight: '100vh', backgroundColor: '#0A0A0A', display: 'flex' }}>
      {/* Sidebar */}
      <aside data-testid="admin-sidebar" className="hidden lg:flex" style={{ width: '250px', backgroundColor: '#141414', borderRight: '1px solid #27272A', flexDirection: 'column', position: 'fixed', height: '100%', zIndex: 20 }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #27272A', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="VGI" style={{ height: '32px' }} />
          <span style={{ color: '#fff', fontWeight: 800, fontSize: '14px' }}>Admin Panel</span>
        </div>
        <nav style={{ flex: 1, paddingTop: '8px' }}>
          {tabs.map(t => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => t.id === "photos" ? navigate("/admin/photos") : t.id === "before-after" ? navigate("/admin/before-after") : setTab(t.id)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 24px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer', transition: 'all 0.2s', backgroundColor: tab === t.id ? 'rgba(139,0,0,0.1)' : 'transparent', color: tab === t.id ? '#8B0000' : '#aaa', borderRight: tab === t.id ? '2px solid #8B0000' : '2px solid transparent', textAlign: 'left' }}>
              <t.icon size={18} /> {t.label}
              {t.id === "quotes" && stats?.new_quotes > 0 && <span style={{ marginLeft: 'auto', backgroundColor: '#8B0000', color: '#fff', fontSize: '10px', fontWeight: 800, padding: '2px 8px', borderRadius: '10px' }}>{stats.new_quotes}</span>}
            </button>
          ))}
        </nav>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #27272A' }}>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#aaa', fontSize: '13px', background: 'none', border: 'none', cursor: 'pointer' }}>
            <LogOut size={16} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile Tab Bar */}
      <div className="lg:hidden" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#141414', borderTop: '1px solid #27272A', display: 'flex', zIndex: 50 }}>
        {tabs.slice(0, 5).map(t => (
          <button key={t.id} onClick={() => t.id === "photos" ? navigate("/admin/photos") : t.id === "before-after" ? navigate("/admin/before-after") : setTab(t.id)}
            style={{ flex: 1, padding: '10px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', fontSize: '10px', background: 'none', border: 'none', cursor: 'pointer', color: tab === t.id ? '#8B0000' : '#aaa' }}>
            <t.icon size={18} /> {t.label}
          </button>
        ))}
      </div>

      {/* Main Content */}
      <main style={{ flex: 1, marginLeft: '250px', padding: '32px', paddingBottom: '80px' }} className="lg:!ml-[250px] max-lg:!ml-0">

        {/* ═══ OVERVIEW ═══ */}
        {tab === "overview" && stats && (
          <div data-testid="admin-overview">
            <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: 900, fontFamily: "'Cabinet Grotesk', sans-serif", marginBottom: '24px' }}>Vue d'ensemble</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              {[
                { label: "Total devis", value: stats.total_quotes, color: "#fff" },
                { label: "Nouveaux", value: stats.new_quotes, color: "#60a5fa" },
                { label: "En cours", value: stats.in_progress, color: "#fbbf24" },
                { label: "Traités", value: stats.completed, color: "#4ade80" },
                { label: "Devis aujourd'hui", value: stats.quotes_today, color: "#c084fc" },
                { label: "Messages aujourd'hui", value: stats.contacts_today, color: "#f472b6" },
                { label: "Messages total", value: stats.total_contacts, color: "#fff" },
                { label: "Photos galerie", value: stats.total_gallery, color: "#fff" },
              ].map(s => (
                <div key={s.label} style={{ backgroundColor: '#141414', border: '1px solid #27272A', borderRadius: '8px', padding: '16px' }}>
                  <p style={{ color: '#666', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>{s.label}</p>
                  <p style={{ color: s.color, fontSize: '28px', fontWeight: 900, fontFamily: "'Cabinet Grotesk', sans-serif", margin: 0 }}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Derniers devis */}
            <Card title="Derniers devis reçus" icon={FileText}>
              {quotes.slice(0, 5).map(q => (
                <div key={q.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #1f1f1f' }}>
                  <div>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>{q.full_name}</span>
                    <span style={{ color: '#666', fontSize: '12px', marginLeft: '12px' }}>{q.project_type}</span>
                  </div>
                  <span style={{ color: '#666', fontSize: '11px' }}>{new Date(q.created_at).toLocaleDateString('fr-BE')}</span>
                </div>
              ))}
            </Card>

            {/* Quick settings status */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Card title="Mode maintenance" icon={Shield} status={settings.maintenance_mode}>
                <p style={{ color: '#666', fontSize: '12px' }}>{settings.maintenance_mode ? "Le site est hors ligne" : "Le site est en ligne"}</p>
              </Card>
              <Card title="Bannière annonce" icon={Bell} status={settings.banner_active}>
                <p style={{ color: '#666', fontSize: '12px' }}>{settings.banner_active ? settings.banner_text : "Aucune bannière active"}</p>
              </Card>
            </div>
          </div>
        )}

        {/* ═══ QUOTES ═══ */}
        {tab === "quotes" && (
          <div data-testid="admin-quotes">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
              <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: 900, fontFamily: "'Cabinet Grotesk', sans-serif", margin: 0 }}>Demandes de devis</h2>
              <div style={{ display: 'flex', gap: '6px' }}>
                {["all", "nouveau", "en_cours", "traite"].map(s => (
                  <button key={s} data-testid={`filter-status-${s}`} onClick={() => setFilterStatus(s)}
                    style={{ padding: '6px 12px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', border: 'none', borderRadius: '4px', cursor: 'pointer', backgroundColor: filterStatus === s ? '#8B0000' : '#1a1a1a', color: filterStatus === s ? '#fff' : '#aaa' }}>
                    {s === "all" ? "Tous" : statusLabels[s]}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filteredQuotes.map(q => (
                <div key={q.id} data-testid={`quote-${q.id}`} style={{ backgroundColor: '#141414', border: '1px solid #27272A', borderRadius: '8px', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ color: '#fff', fontWeight: 700 }}>{q.full_name}</span>
                        <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', backgroundColor: q.status === 'nouveau' ? '#1d4ed8' : q.status === 'en_cours' ? '#a16207' : '#166534', color: '#fff' }}>{statusLabels[q.status]}</span>
                      </div>
                      <p style={{ color: '#8B0000', fontSize: '13px', fontWeight: 600 }}>{q.project_type}</p>
                      <div style={{ display: 'flex', gap: '16px', marginTop: '6px', fontSize: '12px', color: '#666' }}>
                        <span>{q.phone}</span><span>{q.email}</span><span>{new Date(q.created_at).toLocaleDateString('fr-BE')}</span>
                      </div>
                      {q.message && <p style={{ color: '#888', fontSize: '13px', marginTop: '8px', borderTop: '1px solid #222', paddingTop: '8px' }}>{q.message}</p>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <select value={q.status} onChange={e => updateStatus(q.id, e.target.value)}
                        style={{ backgroundColor: '#0a0a0a', border: '1px solid #27272A', color: '#fff', fontSize: '12px', padding: '6px 8px', borderRadius: '4px' }}>
                        <option value="nouveau">Nouveau</option>
                        <option value="en_cours">En cours</option>
                        <option value="traite">Traité</option>
                      </select>
                      <button onClick={() => deleteQuote(q.id)} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredQuotes.length === 0 && <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>Aucun devis</p>}
            </div>
          </div>
        )}

        {/* ═══ CONTACTS ═══ */}
        {tab === "chats" && <AdminChats authHeaders={authHeaders} />}

        {tab === "contacts" && (
          <div data-testid="admin-contacts">
            <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: 900, fontFamily: "'Cabinet Grotesk', sans-serif", marginBottom: '24px' }}>Messages de contact</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {contacts.map(c => (
                <div key={c.id} style={{ backgroundColor: '#141414', border: '1px solid #27272A', borderRadius: '8px', padding: '16px' }}>
                  <span style={{ color: '#fff', fontWeight: 700 }}>{c.first_name} {c.last_name}</span>
                  <span style={{ color: '#8B0000', fontSize: '13px', fontWeight: 600, marginLeft: '12px' }}>{c.work_type}</span>
                  <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '12px', color: '#666' }}>
                    <span>{c.phone}</span><span>{c.email}</span><span>{new Date(c.created_at).toLocaleDateString('fr-BE')}</span>
                  </div>
                  {c.description && <p style={{ color: '#888', fontSize: '13px', marginTop: '8px', borderTop: '1px solid #222', paddingTop: '8px' }}>{c.description}</p>}
                </div>
              ))}
              {contacts.length === 0 && <p style={{ color: '#666', textAlign: 'center', padding: '40px' }}>Aucun message</p>}
            </div>
          </div>
        )}

        {/* ═══ SETTINGS ═══ */}
        {tab === "settings" && (
          <div data-testid="admin-settings">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: 900, fontFamily: "'Cabinet Grotesk', sans-serif", margin: 0 }}>Paramètres du site</h2>
              <SaveBtn onClick={saveSettings} saving={saving} saved={saved} />
            </div>

            {/* Maintenance */}
            <Card title="Mode Maintenance" icon={Shield} status={settings.maintenance_mode}>
              <Toggle checked={settings.maintenance_mode || false} onChange={v => updateSetting('maintenance_mode', v)} label="Activer le mode maintenance" desc="Met le site hors ligne avec un message personnalisé" />
              {settings.maintenance_mode && (
                <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#1a0000', border: '1px solid #8B0000', borderRadius: '6px' }}>
                  <AlertTriangle size={16} color="#f87171" style={{ marginBottom: '8px' }} />
                  <p style={{ color: '#f87171', fontSize: '12px', fontWeight: 700 }}>Le site est actuellement hors ligne !</p>
                </div>
              )}
              <SettingInput label="Message de maintenance" value={settings.maintenance_message || ""} onChange={v => updateSetting('maintenance_message', v)} icon={MessageSquare} />
              <SettingInput label="Heure de retour estimée" value={settings.maintenance_return_time || ""} onChange={v => updateSetting('maintenance_return_time', v)} icon={Clock} placeholder="Ex: 15h00" />
            </Card>

            {/* Bannière */}
            <Card title="Bannière d'annonce" icon={Bell} status={settings.banner_active}>
              <Toggle checked={settings.banner_active || false} onChange={v => updateSetting('banner_active', v)} label="Afficher la bannière" desc="Bannière en haut de toutes les pages" />
              <SettingInput label="Texte de la bannière" value={settings.banner_text || ""} onChange={v => updateSetting('banner_text', v)} icon={Bell} />
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#8B0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Couleur de fond</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="color" value={settings.banner_color || "#8B0000"} onChange={e => updateSetting('banner_color', e.target.value)} style={{ width: '40px', height: '36px', border: 'none', cursor: 'pointer', borderRadius: '4px' }} />
                  <span style={{ color: '#aaa', fontSize: '13px' }}>{settings.banner_color}</span>
                </div>
              </div>
              {settings.banner_active && (
                <div style={{ marginTop: '8px', padding: '10px 16px', backgroundColor: settings.banner_color || '#8B0000', borderRadius: '6px', textAlign: 'center' }}>
                  <p style={{ color: '#fff', fontSize: '13px', fontWeight: 700, margin: 0 }}>{settings.banner_text}</p>
                </div>
              )}
            </Card>

            {/* Coordonnées */}
            <Card title="Coordonnées" icon={Phone}>
              <SettingInput label="Téléphone" value={settings.phone || ""} onChange={v => updateSetting('phone', v)} icon={Phone} />
              <SettingInput label="Email" value={settings.email || ""} onChange={v => updateSetting('email', v)} icon={Mail} />
              <SettingInput label="Adresse" value={settings.address || ""} onChange={v => updateSetting('address', v)} icon={MapPin} />
              <SettingInput label="Horaires" value={settings.hours || ""} onChange={v => updateSetting('hours', v)} icon={Clock} />
            </Card>

            {/* Compteurs */}
            <Card title="Compteurs du site" icon={Hash}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <SettingInput label="Depuis" value={settings.counter_since || ""} onChange={v => updateSetting('counter_since', v)} placeholder="Depuis 2018" />
                <SettingInput label="Chantiers livrés" value={settings.counter_projects || ""} onChange={v => updateSetting('counter_projects', v)} placeholder="67 chantiers livrés" />
                <SettingInput label="Note Google" value={settings.counter_rating || ""} onChange={v => updateSetting('counter_rating', v)} placeholder="5,0/5 sur Google" />
              </div>
            </Card>

            {/* Réseaux sociaux */}
            <Card title="Réseaux sociaux" icon={Link2}>
              <SettingInput label="Facebook" value={settings.social_facebook || ""} onChange={v => updateSetting('social_facebook', v)} icon={Facebook} placeholder="https://facebook.com/..." />
              <SettingInput label="Instagram" value={settings.social_instagram || ""} onChange={v => updateSetting('social_instagram', v)} icon={Instagram} placeholder="https://instagram.com/..." />
              <SettingInput label="WhatsApp" value={settings.social_whatsapp || ""} onChange={v => updateSetting('social_whatsapp', v)} icon={MessageCircle} placeholder="https://wa.me/..." />
              <SettingInput label="LinkedIn" value={settings.social_linkedin || ""} onChange={v => updateSetting('social_linkedin', v)} icon={Linkedin} placeholder="https://linkedin.com/..." />
            </Card>

            <div style={{ marginTop: '16px' }}><SaveBtn onClick={saveSettings} saving={saving} saved={saved} /></div>
          </div>
        )}

        {/* ═══ REVIEWS ═══ */}
        {tab === "reviews" && (
          <div data-testid="admin-reviews">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: 900, fontFamily: "'Cabinet Grotesk', sans-serif", margin: 0 }}>Avis clients</h2>
              <button onClick={() => { setEditReview(null); setReviewForm({ name: "", text: "", rating: 5, active: true }); setEditReview("new"); }}
                style={{ backgroundColor: '#8B0000', color: '#fff', fontWeight: 700, fontSize: '12px', padding: '10px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', textTransform: 'uppercase' }}>
                <Plus size={14} /> Ajouter un avis
              </button>
            </div>

            {editReview && (
              <Card title={editReview === "new" ? "Nouvel avis" : "Modifier l'avis"} icon={Star}>
                <SettingInput label="Nom du client" value={reviewForm.name} onChange={v => setReviewForm(f => ({ ...f, name: v }))} icon={Users} />
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#8B0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Avis</label>
                  <textarea value={reviewForm.text} onChange={e => setReviewForm(f => ({ ...f, text: e.target.value }))} rows={3}
                    style={{ width: '100%', backgroundColor: '#0a0a0a', border: '1px solid #27272A', color: '#fff', padding: '10px', borderRadius: '6px', resize: 'none', fontSize: '14px' }} />
                </div>
                <Toggle checked={reviewForm.active} onChange={v => setReviewForm(f => ({ ...f, active: v }))} label="Avis actif" desc="Affiché sur le site" />
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button onClick={saveReview} style={{ backgroundColor: '#8B0000', color: '#fff', fontWeight: 700, fontSize: '12px', padding: '8px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                    {editReview === "new" ? "Ajouter" : "Sauvegarder"}
                  </button>
                  <button onClick={() => setEditReview(null)} style={{ backgroundColor: '#333', color: '#fff', fontSize: '12px', padding: '8px 20px', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Annuler</button>
                </div>
              </Card>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {reviews.map((r, i) => (
                <div key={r.id} style={{ backgroundColor: '#141414', border: '1px solid #27272A', borderRadius: '8px', padding: '16px', opacity: r.active ? 1 : 0.5 }}>
                  <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '12px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>{r.name}</span>
                        <div style={{ display: 'flex' }}>{[...Array(r.rating || 5)].map((_, j) => <Star key={j} size={12} color="#8B0000" fill="#8B0000" />)}</div>
                        {!r.active && <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', backgroundColor: '#333', color: '#888' }}>Masqué</span>}
                      </div>
                      <p style={{ color: '#888', fontSize: '13px', margin: '4px 0 0' }}>"{r.text}"</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <button onClick={() => reorderReview(r.id, "up")} disabled={i === 0} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', opacity: i === 0 ? 0.3 : 1 }}><ChevronUp size={16} /></button>
                      <button onClick={() => reorderReview(r.id, "down")} disabled={i === reviews.length - 1} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', opacity: i === reviews.length - 1 ? 0.3 : 1 }}><ChevronDown size={16} /></button>
                      <button onClick={() => toggleReview(r)} style={{ color: r.active ? '#4ade80' : '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>{r.active ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                      <button onClick={() => { setEditReview(r); setReviewForm({ name: r.name, text: r.text, rating: r.rating, active: r.active }); }} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}><Pencil size={16} /></button>
                      <button onClick={() => deleteReview(r.id)} style={{ color: '#666', background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ SEO ═══ */}
        {tab === "seo" && (
          <div data-testid="admin-seo">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: 900, fontFamily: "'Cabinet Grotesk', sans-serif", margin: 0 }}>Paramètres SEO</h2>
              <SaveBtn onClick={saveSettings} saving={saving} saved={saved} />
            </div>
            {["home", "about", "services", "realisations", "contact", "zones"].map(page => (
              <Card key={page} title={`Page ${page === "home" ? "Accueil" : page === "about" ? "À propos" : page.charAt(0).toUpperCase() + page.slice(1)}`} icon={Globe}>
                <SettingInput label="Titre de la page" value={settings[`seo_${page}_title`] || ""} onChange={v => updateSetting(`seo_${page}_title`, v)} />
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: '#8B0000', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Description meta</label>
                  <textarea value={settings[`seo_${page}_desc`] || ""} onChange={e => updateSetting(`seo_${page}_desc`, e.target.value)} rows={2}
                    style={{ width: '100%', backgroundColor: '#0a0a0a', border: '1px solid #27272A', color: '#fff', padding: '10px', borderRadius: '6px', resize: 'none', fontSize: '13px' }} />
                </div>
              </Card>
            ))}
            <SaveBtn onClick={saveSettings} saving={saving} saved={saved} />
          </div>
        )}

        {/* ═══ PHOTOS redirect ═══ */}
        {tab === "photos" && (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <Upload size={48} color="#8B0000" style={{ margin: '0 auto 20px' }} />
            <h2 style={{ color: '#fff', fontSize: '24px', fontWeight: 900, fontFamily: "'Cabinet Grotesk', sans-serif", marginBottom: '16px' }}>Gestion des Photos</h2>
            <p style={{ color: '#666', marginBottom: '24px' }}>Uploadez et gérez toutes les photos du site via Cloudinary</p>
            <button onClick={() => navigate("/admin/photos")} style={{ backgroundColor: '#8B0000', color: '#fff', fontWeight: 700, fontSize: '14px', padding: '14px 32px', border: 'none', borderRadius: '6px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ouvrir le gestionnaire de photos
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
